import React from 'react';
import { CalendarDays, TrendingUp, Percent, Briefcase, DollarSign, BarChart2, AlertCircle } from 'lucide-react';
import ToolResult, { ResultItem } from '../../../../../components/ui/ToolResult';
import { formatNumber, formatCurrency } from '../../../../../utils/formatting';

/**
 * Компонент отображения результатов расчета инвестиций
 */
const ResultsOverview = ({ results }) => {
  if (!results) return null;

  // Форматирование дат
  const formatDate = (date) => {
    if (!date) return '';
    return new Date(date).toLocaleDateString('ru-RU', {
      day: '2-digit',
      month: 'long',
      year: 'numeric'
    });
  };

  // Определение класса для отображения показателей в зависимости от значения
  const getValueColorClass = (value, thresholds = { positive: 0, warning: -5 }) => {
    if (value >= thresholds.positive) return 'text-green-600 dark:text-green-400';
    if (value >= thresholds.warning) return 'text-amber-600 dark:text-amber-400';
    return 'text-red-600 dark:text-red-400';
  };

  return (
    <ToolResult title="Результаты инвестирования">
      {/* Ключевые метрики */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mb-6">
        {/* Итоговая стоимость */}
        <div className="rounded-lg bg-gradient-to-br from-indigo-50 to-purple-50 dark:from-indigo-900/30 dark:to-purple-900/30 p-5 border border-indigo-100 dark:border-indigo-800/50 shadow-sm">
          <h3 className="text-base font-medium text-indigo-700 dark:text-indigo-300 mb-1.5 flex items-center gap-1.5">
            <Briefcase className="w-4 h-4"/>
            Итоговая стоимость портфеля
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className="text-3xl font-bold text-indigo-800 dark:text-indigo-200" aria-live="polite">
              {formatCurrency(results.finalPortfolioValue, 0)}
            </span>
            {results.includeInflation && (
              <span className="text-sm font-medium text-indigo-600 dark:text-indigo-400">
                (с учетом инфляции: {formatCurrency(results.inflationAdjustedValue, 0)})
              </span>
            )}
          </div>
          <p className="text-xs text-indigo-600 dark:text-indigo-400 mt-1">
            Инвестировано: {formatCurrency(results.totalInvested, 0)}
          </p>
        </div>

        {/* Доходность */}
        <div className="rounded-lg bg-gradient-to-br from-rose-50 to-orange-50 dark:from-rose-900/30 dark:to-orange-900/30 p-5 border border-rose-100 dark:border-rose-800/50 shadow-sm">
          <h3 className="text-base font-medium text-rose-700 dark:text-rose-300 mb-1.5 flex items-center gap-1.5">
            <TrendingUp className="w-4 h-4"/>
            Общая доходность
          </h3>
          <div className="flex items-baseline flex-wrap gap-x-2">
            <span className={`text-3xl font-bold ${getValueColorClass(results.percentageReturn)}`} aria-live="polite">
              {formatNumber(results.percentageReturn, 2)}%
            </span>
            <span className="text-sm font-medium text-rose-600 dark:text-rose-400">
              ({formatCurrency(results.absoluteReturn, 0)})
            </span>
          </div>
          <p className="text-xs text-rose-600 dark:text-rose-400 mt-1">
            Среднегодовая: {formatNumber(results.annualizedReturn, 2)}% в год 
            {results.includeInflation && (
              <span> (реальная: {formatNumber(results.realAnnualizedReturn, 2)}%)</span>
            )}
          </p>
        </div>
      </div>

      {/* Детализированные результаты */}
      <div className="space-y-3 mb-6">
        <ResultItem 
          label="Инвестиционный горизонт" 
          value={`${results.investmentTerm.years} ${pluralizeYears(results.investmentTerm.years)} (${results.investmentTerm.months} мес.)`} 
          color="indigo" 
          icon={<CalendarDays className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Начальные инвестиции" 
          value={formatCurrency(results.initialInvestment, 0)} 
          color="blue" 
          icon={<Briefcase className="w-4 h-4"/>}
        />
        
        <ResultItem 
          label="Дополнительные взносы" 
          value={formatCurrency(results.totalContributions, 0)} 
          color="blue" 
        />
        
        {results.totalWithdrawals > 0 && (
          <ResultItem 
            label="Снятия средств" 
            value={formatCurrency(results.totalWithdrawals, 0)} 
            color="red" 
          />
        )}
        
        <ResultItem 
          label="Доход от инвестиций" 
          value={formatCurrency(results.absoluteReturn, 0)} 
          color="green" 
        />
        
        {results.totalTaxesPaid > 0 && (
          <ResultItem 
            label="Уплаченные налоги" 
            value={formatCurrency(results.totalTaxesPaid, 0)} 
            color="amber" 
            icon={<DollarSign className="w-4 h-4"/>}
          />
        )}
        
        {results.totalTaxDeductions > 0 && (
          <ResultItem 
            label="Налоговые вычеты" 
            value={formatCurrency(results.totalTaxDeductions, 0)} 
            color="green" 
            icon={<DollarSign className="w-4 h-4"/>}
          />
        )}
        
        {results.totalFeesPaid > 0 && (
          <ResultItem 
            label="Комиссии" 
            value={formatCurrency(results.totalFeesPaid, 0)} 
            color="amber" 
          />
        )}
        
        {results.includeInflation && (
          <ResultItem 
            label="Влияние инфляции" 
            value={formatCurrency(results.totalInflationImpact, 0)} 
            color="red" 
            icon={<Percent className="w-4 h-4"/>}
          />
        )}

        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <div className="flex-1 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <BarChart2 className="w-5 h-5 text-indigo-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Сравнение со стратегиями</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Консервативная</span>
                    <span className="font-medium">{formatCurrency(results.benchmarkComparison.conservativePortfolio.finalValue, 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Сбалансированная</span>
                    <span className="font-medium">{formatCurrency(results.benchmarkComparison.balancedPortfolio.finalValue, 0)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Агрессивная</span>
                    <span className="font-medium">{formatCurrency(results.benchmarkComparison.aggressivePortfolio.finalValue, 0)}</span>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="flex-1 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-4 border border-gray-200 dark:border-gray-700">
            <div className="flex items-start gap-3">
              <div className="mt-0.5">
                <AlertCircle className="w-5 h-5 text-orange-500" />
              </div>
              <div>
                <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-1">Ключевые показатели</h4>
                <div className="space-y-2">
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Период</span>
                    <span className="font-medium">{formatDate(results.startDate)} — {formatDate(results.endDate)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Коэффициент доходность/риск</span>
                    <span className="font-medium">{formatNumber(results.annualizedReturn / 15, 2)}</span>
                  </div>
                  <div className="flex justify-between text-sm">
                    <span className="text-gray-600 dark:text-gray-400">Доходность на вложенный капитал</span>
                    <span className={`font-medium ${getValueColorClass(results.percentageReturn)}`}>
                      {formatNumber(results.percentageReturn, 2)}%
                    </span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ИИС раздел */}
        {results.useIIS && (
          <div className="mt-4 bg-green-50 dark:bg-green-900/20 rounded-lg p-4 border border-green-100 dark:border-green-700">
            <div className="flex items-start gap-3">
              <DollarSign className="w-5 h-5 text-green-500 mt-0.5" />
              <div>
                <h4 className="text-sm font-medium text-green-800 dark:text-green-300 mb-1">
                  Выгода от использования ИИС
                </h4>
                <p className="text-sm text-green-600 dark:text-green-400">
                  {results.iisType === 'taxDeduction' 
                    ? `Налоговый вычет по типу А составил ${formatCurrency(results.totalTaxDeductions, 0)}, что увеличило итоговую доходность на ${formatNumber((results.totalTaxDeductions / results.totalInvested) * 100, 1)}%.`
                    : `Освобождение от НДФЛ по ИИС типа Б сэкономило ${formatCurrency(results.absoluteReturn * 0.13, 0)} налогов, что увеличило итоговую доходность на ${formatNumber(13, 1)}%.`
                  }
                </p>
                <p className="text-xs text-green-600 dark:text-green-400 mt-2">
                  <strong>Важно:</strong> Налоговые льготы по ИИС доступны только резидентам РФ, имеющим официальный доход, облагаемый НДФЛ, и при соблюдении срока инвестирования не менее 3 лет.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
    </ToolResult>
  );
};

// Вспомогательная функция для склонения слова "год" в зависимости от числа
const pluralizeYears = (years) => {
  const lastDigit = years % 10;
  const lastTwoDigits = years % 100;
  
  if (lastTwoDigits >= 11 && lastTwoDigits <= 19) return 'лет';
  if (lastDigit === 1) return 'год';
  if (lastDigit >= 2 && lastDigit <= 4) return 'года';
  return 'лет';
};

export default ResultsOverview;